import React, { useContext, useMemo } from "react";
import {
  BasicAccountApplicationDocument,
  NaturalPersonDocument,
} from "../../api/commercial/generated-types";
import FlowHeader from "../../ui/widgets/flow-header";
import ProductImage from "../shared/components/product-image";
import { useMessageSource } from "react-message-source";
import { ProductContext } from "../../context/product-context/productContext";
import TypographyContainer from "../../ui/layout/page/typography-container/typography-base-container";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import ContentHeader from "../../ui/layout/page/content/header";
import { Form, Formik } from "formik";
import Typography from "../../ui/widgets/typography";
import ContentFooter from "../../ui/layout/page/content/footer";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import { CompanyOnlineModel } from "./types";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import AuthorizedRepresentatives from "./authorized-representatives";
import { StatementRecipient } from "./statement-recipient";
import { CardsAndPinRecipient } from "./cards-and-pin-recipient";
import SectionFullWidthContainer from "../../ui/layout/page/forms-container/section-full-width-container";
import { SecurityCode } from "./security-code";
import { MarketingEmails } from "./marketing-emails";

export interface CompanyOnlineProps {
  authorizedRepresentatives: NaturalPersonDocument[];
  companyName: BasicAccountApplicationDocument["company"]["name"];
  initialValues: CompanyOnlineModel;
  goBack: () => void;
  goNext: (values: CompanyOnlineModel) => void;
}

export const CompanyOnline: React.FC<CompanyOnlineProps> = ({
  authorizedRepresentatives,
  companyName,
  initialValues,
  goBack,
  goNext,
}: CompanyOnlineProps) => {
  const { getMessage } = useMessageSource();
  const { selectedProduct } = useContext(ProductContext);
  const { setCancelDialogActive } = useContext(CancelApplicationContext);

  const hasAuthorizedRepresentatives: boolean = useMemo(
    () => Boolean(authorizedRepresentatives.length > 0),
    [authorizedRepresentatives],
  );

  return (
    <>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={companyName}
      />

      <ApplicationPageContent>
        <TypographyContainer>
          <TypographyItemContainer>
            <ContentHeader title={getMessage("CompanyOnline-title")} />
          </TypographyItemContainer>
          <Typography variant="body1">
            {getMessage("CompanyOnline-description")}
          </Typography>
        </TypographyContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, formikActions) => {
            goNext(values);
            formikActions.setSubmitting(false);
          }}
        >
          {({ isSubmitting, errors, submitCount, handleSubmit }) => (
            <Form>
              <AuthorizedRepresentatives
                authorizedRepresentatives={authorizedRepresentatives}
              />
              {hasAuthorizedRepresentatives && (
                <SectionFullWidthContainer hasAdditionalTopMargin>
                  <TypographyContainer>
                    <Typography
                      variant="h4"
                      className="scd-container-base-small"
                    >
                      {getMessage(
                        "CompanyOnline-statement-cards-and-pin-delivery.text",
                      )}
                    </Typography>
                  </TypographyContainer>

                  <StatementRecipient
                    authorizedRepresentatives={authorizedRepresentatives}
                  />
                  <CardsAndPinRecipient
                    authorizedRepresentatives={authorizedRepresentatives}
                  />
                </SectionFullWidthContainer>
              )}
              {hasAuthorizedRepresentatives && <SecurityCode />}
              <MarketingEmails />
              <ContentFooter>
                <InvalidFormError
                  isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                />
                <NavigationBar
                  onCancel={() => setCancelDialogActive(true)}
                  cancelButtonText={getMessage("Onboarding.cancel")}
                  isSubmitting={isSubmitting}
                  backButtonText={getMessage("Onboarding.back")}
                  nextButtonText={getMessage("Onboarding.continue")}
                  onBack={goBack}
                  onNext={handleSubmit}
                />
              </ContentFooter>
            </Form>
          )}
        </Formik>
      </ApplicationPageContent>
    </>
  );
};
